import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Filter, Search as SearchIcon, X } from "lucide-react";

const CATEGORIES = [
  { slug: "all", name: "All" },
  { slug: "candles", name: "Candles" },
  { slug: "figurines", name: "Figurines" },
  { slug: "diy-kits", name: "DIY Kits" },
  { slug: "floral-bouquets", name: "Floral Bouquets" },
  { slug: "home-decor", name: "Home Decor" },
  { slug: "garden-decor-accessories", name: "Garden Décor & Accessories" },
  { slug: "little-joys-kids", name: "Little Joys for Kids" },
];

const PRICE_RANGES = [
  { key: "all", label: "Any price", min: 0, max: Infinity },
  { key: "under-500", label: "Under ₹500", min: 0, max: 500 },
  { key: "500-1000", label: "₹500 – ₹1000", min: 500, max: 1000 },
  { key: "1000-2000", label: "₹1000 – ₹2000", min: 1000, max: 2000 },
  { key: "2000-plus", label: "₹2000+", min: 2000, max: Infinity },
];

export default function Shop() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = category || "all";
  const searchTerm = searchParams.get("search") || "";
  const isSearchMode = !!searchTerm;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [priceRange, setPriceRange] = useState("all");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory !== "all") params.set("category", activeCategory);
    if (searchTerm) params.set("search", searchTerm);
    api.get(`/products?${params.toString()}`).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [activeCategory, searchTerm]);

  const filteredSorted = useMemo(() => {
    const range = PRICE_RANGES.find((r) => r.key === priceRange) || PRICE_RANGES[0];
    let list = products.filter((p) => p.price >= range.min && p.price <= range.max);
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "newest") list.sort((a, b) => (b.is_new === true) - (a.is_new === true));
    else list.sort((a, b) => (b.is_featured === true) - (a.is_featured === true));
    return list;
  }, [products, sort, priceRange]);

  const currentName = CATEGORIES.find(c => c.slug === activeCategory)?.name || "All";

  const clearSearch = () => {
    setSearchParams({});
  };

  return (
    <div data-testid="shop-page">
      <section className="border-b border-[#E3DCD2] bg-[#F3EFEA]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          {isSearchMode ? (
            <>
              <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3 flex items-center gap-2">
                <SearchIcon className="w-3 h-3" strokeWidth={2} />
                ✦ Search results
              </div>
              <div className="flex items-baseline flex-wrap gap-4">
                <h1 className="font-serif-display text-5xl md:text-6xl leading-tight">
                  "{searchTerm}"
                </h1>
                <button
                  onClick={clearSearch}
                  className="flex items-center gap-1.5 text-xs uppercase tracking-widest-plus text-[#9BA595] hover:text-[#D27D60] transition-colors"
                  data-testid="search-clear-btn"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Clear search
                </button>
              </div>
              <p className="text-sm text-[#4a4a4a] mt-3" data-testid="search-count">
                {loading ? "Searching..." : `${filteredSorted.length} ${filteredSorted.length === 1 ? "match" : "matches"} in our studio`}
              </p>
            </>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Shop</div>
              <h1 className="font-serif-display text-5xl md:text-6xl leading-tight">{currentName}</h1>
            </>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-10 pb-4 border-b border-[#E3DCD2]">
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={c.slug === "all" ? "/shop" : `/shop/${c.slug}`}
                data-testid={`shop-filter-${c.slug}`}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest-plus transition-colors ${
                  activeCategory === c.slug
                    ? "bg-[#1A1A1A] text-[#FDFBF7]"
                    : "border border-[#E3DCD2] hover:border-[#D27D60] hover:text-[#D27D60]"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={priceRange}
              onChange={(e) => setPriceRange(e.target.value)}
              className="bg-transparent border border-[#E3DCD2] rounded-full px-4 py-2 text-xs uppercase tracking-widest-plus outline-none focus:border-[#D27D60] transition-colors"
              data-testid="shop-price-filter"
            >
              {PRICE_RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#9BA595]" strokeWidth={1.5} />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-transparent border border-[#E3DCD2] rounded-full px-4 py-2 text-xs uppercase tracking-widest-plus outline-none focus:border-[#D27D60] transition-colors"
                data-testid="shop-sort-select"
              >
                <option value="featured">Featured</option>
                <option value="newest">Newest</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[0,1,2,3,4,5,6,7].map(i => (
              <div key={i} className="animate-pulse aspect-[4/5] bg-[#F3EFEA] rounded-md" />
            ))}
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="text-center py-24" data-testid="shop-empty-state">
            <p className="font-serif-display text-3xl mb-2">
              {isSearchMode ? `No candles matched "${searchTerm}"` : "No products found"}
            </p>
            <p className="text-sm text-[#9BA595] mb-6">
              {isSearchMode ? "Try a different word, or explore by category below." : "Try a different filter."}
            </p>
            <Link to="/shop" className="text-sm underline underline-offset-8 hover:text-[#D27D60]" data-testid="shop-empty-back">
              View all products
            </Link>
          </div>
        ) : (
          <>
            <div className="text-xs text-[#9BA595] mb-6" data-testid="shop-product-count">
              {filteredSorted.length} {filteredSorted.length === 1 ? "product" : "products"}
              {priceRange !== "all" && ` · ${PRICE_RANGES.find(r => r.key === priceRange).label}`}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
              {filteredSorted.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
